/* ReliefYou photo reel (sections/ry-photo-reel.liquid). Styles: assets/ry-reel.css.
   - Pause motion toggle (WCAG 2.2.2): aria-pressed, remembered for the visit in sessionStorage and applied
     to every reel and ticker (assets/ry-ticker.js) on the page.
   - Pauses the animation while the reel is off-screen (class is-offscreen), to save battery.
   - Loads the photos about one row-width ahead: once the reel is near the screen, a second observer (root = the
     clipped row) switches each lazy photo to eager when it comes within one row-width of the visible part
     (the clipped row hides the cards from the browser's own lazy loading). Photos that never come near are
     never downloaded.
   - Reduced motion (and the arc in browsers without CSS sin()): the row becomes a scroll region, so it gets
     tabindex, a role and a name (keyboard scroll).
   - Theme editor: a selected photo block stops the row and brings its card into view (class is-editing; the
     arc turns to it, the straight row scrolls to it).
   The animation itself is CSS and only runs once this element is defined, so the toggle always works. */
(() => {
  if (customElements.get('ry-reel')) return;

  const STORAGE_KEY = 'ry-reel-paused';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const supportsArc = !!(window.CSS && CSS.supports && CSS.supports('width', 'calc(1px * sin(1deg))'));

  const readPaused = () => {
    try {
      return window.sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch (error) {
      return false;
    }
  };

  const writePaused = (paused) => {
    try {
      if (paused) {
        window.sessionStorage.setItem(STORAGE_KEY, '1');
      } else {
        window.sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      /* Storage blocked (private mode, site data off): the toggle still works for this page. */
    }
  };

  class RyReel extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-ry-reel-toggle]');
      this.viewport = this.querySelector('[data-ry-reel-viewport]');

      this.setPaused(readPaused());

      this.onToggle = () => {
        const paused = this.toggle.getAttribute('aria-pressed') !== 'true';
        writePaused(paused);
        // One "Pause motion" state for the page: reels and tickers (assets/ry-ticker.js) share it.
        document.querySelectorAll('ry-reel, ry-ticker').forEach((element) => {
          if (typeof element.setPaused === 'function') element.setPaused(paused);
        });
      };
      if (this.toggle) this.toggle.addEventListener('click', this.onToggle);

      this.onMotionChange = () => this.syncMotion();
      if (reduceMotion.addEventListener) {
        reduceMotion.addEventListener('change', this.onMotionChange);
      } else if (reduceMotion.addListener) {
        reduceMotion.addListener(this.onMotionChange);
      }
      this.syncMotion();

      // Theme editor: stop and show the selected photo block's card.
      this.onBlockSelect = (event) => {
        if (!this.contains(event.target)) return;
        const item = event.target.closest('.ry-reel__item') || event.target;
        const index = item.style ? item.style.getPropertyValue('--i').trim() : '';
        if (index) this.style.setProperty('--ry-reel-focus', index);
        this.classList.add('is-editing');
        this.loadImages(true);
        // Straight row or scroller: scroll the row to the card (the arc turns to it in CSS).
        if (window.getComputedStyle(item).position !== 'absolute' && item.scrollIntoView) {
          item.scrollIntoView({ block: 'nearest', inline: 'center' });
        }
      };
      this.onBlockDeselect = (event) => {
        if (!this.contains(event.target)) return;
        this.classList.remove('is-editing');
        this.style.removeProperty('--ry-reel-focus');
        if (this.viewport && !this.isScroller()) this.viewport.scrollLeft = 0;
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.classList.toggle('is-offscreen', !entry.isIntersecting);
              if (entry.isIntersecting) this.loadImages(false);
            });
          },
          { rootMargin: '300px 0px' }
        );
        this.observer.observe(this);
      } else {
        this.loadImages(true);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.imgObserver) this.imgObserver.disconnect();
      this.imgObserver = null;
      if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      if (reduceMotion.removeEventListener) {
        reduceMotion.removeEventListener('change', this.onMotionChange);
      } else if (reduceMotion.removeListener) {
        reduceMotion.removeListener(this.onMotionChange);
      }
    }

    setPaused(paused) {
      this.classList.toggle('is-paused', paused);
      if (this.toggle) this.toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
    }

    // Per photo, about one row-width ahead of the visible part (all = every photo at once: theme editor,
    // browsers without IntersectionObserver).
    loadImages(all) {
      const lazy = this.querySelectorAll('img[loading="lazy"]');
      if (all || !('IntersectionObserver' in window) || !this.viewport) {
        if (this.imgObserver) this.imgObserver.disconnect();
        lazy.forEach((img) => {
          img.loading = 'eager';
        });
        return;
      }
      if (this.imgObserver || !lazy.length) return;
      this.imgObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.loading = 'eager';
            if (this.imgObserver) this.imgObserver.unobserve(entry.target);
          });
        },
        // Sideways one row-width either side; below by one row height (the arc's next cards rise from there).
        { root: this.viewport, rootMargin: '0px 100% 100% 100%' }
      );
      lazy.forEach((img) => this.imgObserver.observe(img));
    }

    isScroller() {
      return reduceMotion.matches || (this.dataset.shape === 'arc' && !supportsArc);
    }

    syncMotion() {
      if (!this.viewport) return;
      if (this.isScroller()) {
        this.viewport.setAttribute('tabindex', '0');
        this.viewport.setAttribute('role', 'region');
        if (this.viewport.dataset.label) this.viewport.setAttribute('aria-label', this.viewport.dataset.label);
      } else {
        this.viewport.removeAttribute('tabindex');
        this.viewport.removeAttribute('role');
        this.viewport.removeAttribute('aria-label');
      }
    }
  }

  customElements.define('ry-reel', RyReel);
})();
